import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { WordmarkStacked } from "../components/Wordmark";

const SSO_ERROR_MESSAGES = {
  token_expired:        "Votre session a expiré. Reconnectez-vous depuis l'app.",
  token_invalid:        "Lien d'accès invalide. Reconnectez-vous depuis l'app.",
  session_revoked:      "Votre session n'est plus valide. Contactez votre Direction.",
  supabase_unreachable: "Service d'authentification temporairement indisponible.",
  unknown_member:       "Compte non reconnu dans le réseau APOLLO.",
  account_inactive:     "Compte inactif. Contactez votre Direction.",
  account_guest:        "Compte invité — accès Break News non autorisé.",
  account_invalid:      "Statut de compte non valide pour Break News.",
  not_eligible:         "Compte non éligible à Break News.",
  onboarding_failed:    "Erreur d'initialisation du profil. Réessayez.",
  server_config_error:  "Erreur de configuration serveur. Notre équipe a été alertée.",
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const err = params.get("error");
    if (err && SSO_ERROR_MESSAGES[err]) {
      toast.error(SSO_ERROR_MESSAGES[err]);
      // Nettoyer l'URL pour éviter de re-afficher au refresh
      params.delete("error");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/auth/magic-link/request", { email: email.trim() });
      setSubmitted(true);
    } catch {
      setSubmitted(true); // toujours générique
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "#FAFAFA", fontFamily: "'DM Sans', system-ui, sans-serif" }}
      data-testid="login-page"
    >
      <div
        className="w-full max-w-md bg-white rounded-xl border p-10"
        style={{ borderColor: "#E5E5E5", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
      >
        <div className="mb-10 flex flex-col items-center" style={{ fontFamily: "'Sora', sans-serif" }}>
          <WordmarkStacked />
          <p className="mt-4 text-sm text-center" style={{ color: "#666" }}>
            La communication interne du réseau APOLLO
          </p>
        </div>

        {!submitted ? (
          <form onSubmit={submit} data-testid="login-form">
            <label className="block mb-2 text-sm font-medium" style={{ color: "#1A1A1A" }}>
              Votre email APOLLO
            </label>
            <div className="relative mb-5">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2" color="#666" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="prenom.nom@apollo-business.com"
                data-testid="login-email-input"
                className="w-full pl-10 pr-4 py-3 rounded-lg border outline-none transition-colors focus:border-orange-500"
                style={{ borderColor: "#E5E5E5", fontSize: "15px" }}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full py-3 rounded-full font-semibold text-white flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ background: "#F77F00", fontSize: "15px" }}
            >
              {loading ? "Envoi…" : "Recevoir mon lien de connexion"}
              {!loading && <ArrowRight size={18} />}
            </button>
            <p className="mt-6 text-xs text-center" style={{ color: "#666" }}>
              Nous vous enverrons un lien sécurisé valable 15 minutes.
            </p>
          </form>
        ) : (
          <div data-testid="login-success" className="text-center py-4">
            <CheckCircle2 className="mx-auto mb-4" size={48} color="#10B981" />
            <h2 className="text-xl font-semibold mb-2" style={{ color: "#1A1A1A", fontFamily: "'Sora', sans-serif" }}>
              Vérifiez votre boîte mail
            </h2>
            <p style={{ color: "#666" }}>
              Si <strong>{email}</strong> est associé à un compte actif, vous recevrez un lien de connexion dans quelques instants.
            </p>
            <button
              onClick={() => { setSubmitted(false); setEmail(""); }}
              data-testid="login-retry-button"
              className="mt-6 text-sm underline"
              style={{ color: "#F77F00" }}
            >
              Essayer une autre adresse
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
